import { Feature, Polygon } from 'geojson';
import { featureCollection, polygon } from '@turf/helpers';
import intersect from '@turf/intersect';
import bbox from '@turf/bbox';

import { IArea } from "./types/IArea"
import { PolygonUtils } from './util/PolygonUtils';
import { AreaGridUtils } from './util/AreaGridUtils';
import { IStructure } from './types/IStructure';
import { ILayer } from './types/ILayer';
import { DigitalElevationModel } from './services/DigitalElevationModel';
import { LayerUtils } from './util/LayerUtils';
import { IOrganixGridPoint } from './types/IOrganixGridPoint';

interface IBBox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

class GridProcessor {
  // These are initialized at the top of run() rather than in the constructor
  // because they depend on user-driven area dimensions and on the bathymetry
  // layer's min/max. The `!` definite-assignment assertion is correct because
  // run() is the public entry point and run() always sets them before they're
  // read by step()/calc().
  private horizontal_resolution!: number;
  private vertical_resolution!: number;
  private lngStep!: number;
  private latStep!: number;

  private ctx: CanvasRenderingContext2D;
  private structures: IStructure[];
  private area: IArea;
  // Optional: when no bathymetry/current-speed layer is selected, idw is
  // skipped entirely (calculateDepth/calculateUV return null).
  private bathymetryLayer: ILayer | undefined;
  private currentSpeedLayer: ILayer | undefined;
  private dem: DigitalElevationModel;
  private polys!: Feature<Polygon>[];
  private bboxes!: IBBox[];
  private data!: IOrganixGridPoint[];
  private pixelSize: number;
  private minDepth!: number;
  private maxDepth!: number;

  private onProgress: (percentDone: number) => void;
  private onComplete: (data: IOrganixGridPoint[]) => void;

  constructor(
    ctx: CanvasRenderingContext2D, structures: IStructure[], area: IArea,
    bathymetryLayer: ILayer | undefined, currentSpeedLayer: ILayer | undefined,
    pixelSize: number,
    onProgress: (percentDone: number) => void, onComplete: (data: IOrganixGridPoint[]) => void
  ) {
    this.ctx = ctx;
    this.structures = structures;
    this.area = area;
    this.bathymetryLayer = bathymetryLayer;
    this.currentSpeedLayer = currentSpeedLayer;
    this.pixelSize = pixelSize;
    this.dem = new DigitalElevationModel(this.area.resolution, this.area.rect.maxLng - this.area.rect.minLng);
    this.onProgress = onProgress;
    this.onComplete = onComplete;
  }

  // IDW for layers with a single value per point
  private idw = (lng: number, lat: number, power: number, layer: ILayer) => {
    let top = 0;
    let bot = 0;
    layer.data.forEach(dp => {
      const dist = Math.sqrt(Math.pow(dp.lng - lng, 2) + Math.pow(dp.lat - lat, 2));
      top += dp.value[0] / Math.pow(dist, power);
      bot += 1 / Math.pow(dist, power);
    });
    if (bot == 0) { //When the distance between point and sensor is 0 (the point IS the sensor) Gives black spots, so fix this   
      return top;
    } else {
      return (top / bot);
    }
  }

  // IDW for layers with two values per point
  private idw2 = (lng: number, lat: number, power: number, layer: ILayer): number[] => {
    let top = [0,0];
    let bot = 0;
    layer.data.forEach(dp => {
      const dist = Math.sqrt(Math.pow(dp.lng - lng, 2) + Math.pow(dp.lat - lat, 2));
      top[0] += dp.value[0] / Math.pow(dist, power);
      top[1] += dp.value[1] / Math.pow(dist, power);
      bot += 1 / Math.pow(dist, power);
    });
    if (bot == 0) { //When the distance between point and sensor is 0 (the point IS the sensor) Gives black spots, so fix this   
      return top;
    } else {
      return top.map(t => t / bot);
    }
  }    

  // Do two bounding boxes overlap?
  private checkOverlap = (bbox1: IBBox, bbox2: IBBox): boolean => {
    const aLeftOfB = bbox1.maxLng < bbox2.minLng;
    const aRightOfB = bbox1.minLng > bbox2.maxLng;
    const aAboveB = bbox1.minLat > bbox2.maxLat;
    const aBelowB = bbox1.maxLat < bbox2.minLat;
    return !( aLeftOfB || aRightOfB || aAboveB || aBelowB );
  }  

  // Convert a structure to a polygon.
  private structureToPolygon = (structure: IStructure): Feature<Polygon> => {
    if(structure.type == 'polygon') {
      return polygon([[...structure.points.map(p => [ p.lng, p.lat]), [structure.points[0].lng, structure.points[0].lat]]]);
    } else { // circle
      return PolygonUtils.getCirclePolygon(structure.point, structure.radius);
    }
  }  

  private calculateDepth = (lng: number, lat: number): number | null => {
    return this.bathymetryLayer ? this.idw(lng, lat, 3, this.bathymetryLayer) : null;
  }

  private calculateUV = (lng: number, lat: number): number[] | null => {
    return this.currentSpeedLayer ? this.idw2(lng, lat, 3, this.currentSpeedLayer) : null;
  }

  private calculateStructure = (x: number, y: number, lng: number, lat: number): number | null => {
    const cellBbox: IBBox = { 
      minLng: lng - 0.5 * this.lngStep,
      maxLng: lng + 0.5 * this.lngStep,
      minLat: lat - 0.5 * this.latStep,
      maxLat: lat + 0.5 * this.latStep
    }

    // Check cellBBox against all structure bboxes:
    for(let i = 0; i < this.bboxes.length; i++) {
      // Bboxes overlap?
      if(this.checkOverlap(this.bboxes[i], cellBbox)) {
        // Calculate a polygon for this cell to see if the actual polygon overlaps the cell bbox:
        const cellPoly = polygon([[
          [lng - 0.5 * this.lngStep, lat - 0.5 * this.latStep],
          [lng + 0.5 * this.lngStep, lat - 0.5 * this.latStep],
          [lng + 0.5 * this.lngStep, lat + 0.5 * this.latStep],
          [lng - 0.5 * this.lngStep, lat + 0.5 * this.latStep],
          [lng - 0.5 * this.lngStep, lat - 0.5 * this.latStep]
        ]]);   
        if(intersect(featureCollection([this.polys[i], cellPoly])) != null) return i;
        // TODO: do something with the intersection result (which is a polygon).

        // Old boolean intersection:
        // if(booleanIntersects(this.polys[i], cellPoly)) return i;
      }
    }

    return null;
  }

  private drawPixel = (x: number, y: number, color: string) => {
    if(this.pixelSize == 1) {
      this.ctx.fillStyle = color;
      this.ctx.fillRect(x, y, 1, 1);
    } else {
      this.ctx.fillStyle = color;
      this.ctx.fillRect(x * this.pixelSize, y * this.pixelSize, this.pixelSize, this.pixelSize);
    }    
  }

  private calc = async (x: number, y: number) => {
    const lat = this.area.rect.minLat + (y + 0.5) * this.latStep;
    const lng = this.area.rect.minLng + (x + 0.5) * this.lngStep;

    // Perform calculations:
    // Take Mapbox DEM into account. queryElevation can return null when no
    // DEM data is available (no sku captured yet); treat that as "above the
    // threshold" — i.e. land — so the cell is excluded from the simulation.
    const elevation = await this.dem.queryElevation(lng, lat);
    const isWater = elevation !== null && elevation < this.area.demThreshold;
    const depth = isWater ? this.calculateDepth(lng, lat) : null;
    // ORGANIX takes depth as positive metres and null for land. The DEM can
    // call a cell water where the interpolated bathymetry is zero or on the
    // land side (along the coast, or where the data codes land as 0); such a
    // cell is land too, or ORGANIX receives depth <= 0 and spreads over it.
    const signedDepth = depth === null ? null : (this.area.bathymetry == 'negative' ? -depth : depth);
    const waterDepth = signedDepth !== null && signedDepth > 0 ? signedDepth : null;

    // const color = elevation > 0 ? 255 : 0;
    const uv = this.calculateUV(lng, lat);
    const structureIndex = this.calculateStructure(x, y, lng, lat);

    // Draw pixel:
    if(structureIndex != null) {
      this.drawPixel(x,y, `rgba(255,0,0,0.5)`);
    } else if(depth != null && waterDepth != null) {
      const depth255 = 200 - Math.floor((depth - this.minDepth) / (this.maxDepth - this.minDepth) * 200);
      const color = `rgba(${depth255},${depth255},${depth255}, 0.5)`;
      this.drawPixel(x,y,color);
    } else {
      this.drawPixel(x,y,'rgba(0,255,128,0.5)');
    }

    // Store data point:
    this.data.push({
      x: x + 1,
      y: y + 1,
      latitude: lat,
      longitude: lng,
      depth: waterDepth,
      u: (waterDepth !== null && uv) ? uv[0] : null,
      v: (waterDepth !== null && uv) ? uv[1] : null,
      structureIndex: structureIndex != null ? structureIndex + 1 : null,
      stocking: structureIndex == null ? null : parseInt(this.structures[structureIndex].stocking as any)
    });
  }

  private step = async (y: number) => {
    for(let x = 0; x < this.horizontal_resolution; x++) {
      await this.calc(x,y);
    }
    this.onProgress(y / this.vertical_resolution * 100);
    setTimeout(() => {
      y++;
      if(y < this.vertical_resolution) {
        this.step(y);
      } else {
        this.finish();
      }
    }, 0);
  }

  public run = async () => {
    this.horizontal_resolution = this.area.resolution;
    this.vertical_resolution = AreaGridUtils.verticalResolution(this.area.rect, this.area.resolution);
    this.lngStep = (this.area.rect.maxLng - this.area.rect.minLng) / this.horizontal_resolution;
    this.latStep = (this.area.rect.maxLat - this.area.rect.minLat) / this.vertical_resolution;

    // Calculate min, max depth (defaults to 0/0 when no bathymetry layer).
    if (this.bathymetryLayer) {
      [this.minDepth, this.maxDepth] = LayerUtils.getMinMax(this.bathymetryLayer);
    } else {
      this.minDepth = 0;
      this.maxDepth = 0;
    }

    // Calculate a polygon for each structure:
    this.polys = this.structures.map(s => this.structureToPolygon(s));

    // Calculate a bbox for each structure:
    this.bboxes = this.polys.map(p => {
      const res = bbox(p);
      return { minLng: res[0], minLat: res[1], maxLng: res[2], maxLat: res[3] };
    });    

    this.data = [];
    this.step(0);
  }

  public finish = () => {
    this.onComplete(this.data);
  }
}

export { GridProcessor }

